import React, { Component } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import styled from '@emotion/styled';

// Utils
import { truncate } from 'Style/truncate';
import DateUtil from 'Utils/date-util';
import {
  getSectionPath,
  getStoryboardAnalyticsUrl,
  getStoryboardEditUrl,
} from 'Utils/content/flipboard-urls';
import { hasAnalytics } from 'Utils/content/flout-util';
import { KEYS } from 'Utils/window-key-down';
import { getImageObject } from 'Utils/image-util';
import { pushPathToHistory } from 'Utils/history';
import { USAGE_NAV_FROMS, USAGE_EVENT_NAMES } from 'Utils/analytics/usage';

// Components
import { NavFromContext } from 'ComponentLibrary/context';
import SectionLink from './section-link';
import WebLink from './web-link';
import AnalyticsIcon from 'ComponentLibrary/icons/analytics';
import EditIcon from 'ComponentLibrary/icons/edit';
import ClockIcon from 'ComponentLibrary/icons/clock';
import AnchoredDialog, {
  ANCHORED_DIALOG_POSITION_X,
  ANCHORED_DIALOG_POSITION_Y,
} from 'ComponentLibrary/utility/anchored-dialog';
import Image from './base/image';
import RefWrapper from 'ComponentLibrary/utility/ref-wrapper';

import withSelected from '../hocs/withSelected';
import withNavFrom from '../hocs/withNavFrom';
import withT from 'ComponentLibrary/hocs/withT';
import withHistory from 'Webapp/shared/app/hocs/withHistory';

import connector from 'Utils/connector';
import connectCurrentUser from 'Webapp/shared/app/connectors/connectCurrentUser';
import connectUsageSetNavFrom from 'Webapp/shared/app/connectors/connectUsageSetNavFrom';
import connectUsageTrackSectionTap from 'Webapp/shared/app/connectors/connectUsageTrackSectionTap';
import connectLanguage from 'Webapp/shared/app/connectors/connectLanguage';

const StyledSectionLink = styled(SectionLink)({
  '.section-tiles__title': truncate('92%', 5),
});
class StoryboardTile extends Component {
  constructor(props) {
    super(props);

    this.state = { isPublishTooltipOpen: false, isAnalyticsTooltipOpen: false };
    this.tile = React.createRef();
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleMouseEnterSchedule = this.handleMouseEnterSchedule.bind(this);
    this.handleMouseLeaveSchedule = this.handleMouseLeaveSchedule.bind(this);
    this.handleMouseEnterAnalytics = this.handleMouseEnterAnalytics.bind(this);
    this.handleMouseLeaveAnalytics = this.handleMouseLeaveAnalytics.bind(this);

    this.clockIconElement = React.createRef();
    this.analyticsIconElement = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedId === this.props.selectedId) {
      return;
    }
    if (this.props.section.remoteid === this.props.selectedId) {
      this.tile.current.focus();
    }
  }

  handleKeyPress(event) {
    switch (event.keyCode) {
      case KEYS.ENTER: {
        const { history, section } = this.props;
        const sectionPath = getSectionPath(section);
        sectionPath && pushPathToHistory(sectionPath, history);
        break;
      }
    }
  }

  handleMouseEnterSchedule() {
    this.setState({ isPublishTooltipOpen: true });
  }

  handleMouseLeaveSchedule() {
    this.setState({ isPublishTooltipOpen: false });
  }

  handleMouseEnterAnalytics() {
    this.setState({ isAnalyticsTooltipOpen: true });
  }

  handleMouseLeaveAnalytics() {
    this.setState({ isAnalyticsTooltipOpen: false });
  }

  render() {
    const {
      section,
      selectedId,
      shownInFeed,
      currentUser,
      isOwnProfile,
      lang,
      usageSetNavFrom,
      usageTrackSectionTap,
      t,
    } = this.props;

    const isSelected = selectedId === section.remoteid;
    const { title } = section;
    const selectedClass = className({
      'section-tiles__tile-wrapper': true,
      'section-tiles__tile-wrapper--selected': isSelected,
    });
    const tileClasses = className({
      'section-tiles__tile': true,
      'section-tiles__tile--in-feed': shownInFeed,
      'media-link': true,
    });
    const titleClasses = className({
      'section-tiles__title': true,
      'ui-text--title--small': !shownInFeed,
      'ui-text--title': shownInFeed,
    });

    const alt = title ? `${title} - ${t('cover')}` : t('storyboard_cover');

    const tileProps = { ref: this.tile };
    if (isSelected) {
      tileProps.tabIndex = 0;
      tileProps.onKeyDown = this.handleKeyPress;
    }
    const image = section.image || getImageObject(section.coverUrl);

    return (
      <div {...tileProps} className={selectedClass}>
        <StyledSectionLink
          className={tileClasses}
          section={section}
          onClick={usageTrackSectionTap}
        >
          <div className="section-tiles__image">
            {image && <Image image={image} alt={alt} crop />}
          </div>
          <div className="section-tiles__tile-overlay" />
          <div className="section-tiles__tile-content">
            <h3 className={titleClasses}>{title}</h3>
          </div>
        </StyledSectionLink>
        <div className="section-tiles__icons">
          {!section.isDraft && isOwnProfile && hasAnalytics(section.timestamp) && (
            <NavFromContext.Provider
              value={USAGE_NAV_FROMS.PROFILE_SECTION_TILE}
            >
              <WebLink
                className="section-tiles__analytics-icon section-tiles__icon"
                href={getStoryboardAnalyticsUrl(
                  currentUser.screenname,
                  section.id,
                )}
                navFromEventName={USAGE_EVENT_NAMES.ANALYTICS_ENTER}
              >
                <AnalyticsIcon color="#fff" />
              </WebLink>
            </NavFromContext.Provider>
          )}
          {!section.isDraft &&
            isOwnProfile &&
            !hasAnalytics(section.timestamp) && (
              <div
                className="section-tiles__analytics-icon section-tiles__icon"
                onMouseEnter={this.handleMouseEnterAnalytics}
                onMouseLeave={this.handleMouseLeaveAnalytics}
              >
                <RefWrapper ref={this.analyticsIconElement}>
                  <AnalyticsIcon
                    className="section-tiles__analytics-icon--disabled"
                    color="#fff"
                  />
                </RefWrapper>
                {this.state.isAnalyticsTooltipOpen && (
                  <AnchoredDialog
                    targetRef={this.analyticsIconElement}
                    className="storyboard-tile__icon-tooltip"
                    positionX={ANCHORED_DIALOG_POSITION_X.RIGHT}
                    positionY={ANCHORED_DIALOG_POSITION_Y.BELOW}
                  >
                    <span className=" ui-text--supporting">
                      {t('curator-pro-analytics-link-disabled')}
                    </span>
                  </AnchoredDialog>
                )}
              </div>
            )}
          {section.isDraft && isOwnProfile && section.publishAt && (
            <div
              className="section-tiles__analytics-icon section-tiles__icon"
              onMouseEnter={this.handleMouseEnterSchedule}
              onMouseLeave={this.handleMouseLeaveSchedule}
            >
              <RefWrapper ref={this.clockIconElement}>
                <ClockIcon color="#fff" />
              </RefWrapper>
              {this.state.isPublishTooltipOpen && (
                <AnchoredDialog
                  targetRef={this.clockIconElement}
                  className="storyboard-tile__icon-tooltip"
                  styleVariation={AnchoredDialog.styleVariations.TOOLTIP}
                  positionX={ANCHORED_DIALOG_POSITION_X.RIGHT}
                  positionY={ANCHORED_DIALOG_POSITION_Y.BELOW}
                >
                  <span className=" ui-text--supporting">
                    {t('curator_pro_scheduled_time_hint', {
                      time: DateUtil.completeFormat(
                        section.publishAt / 1000,
                        lang,
                        true,
                        true,
                      ),
                    })}
                  </span>
                </AnchoredDialog>
              )}
            </div>
          )}
          {isOwnProfile && (
            <span className="section-tiles__edit-icon section-tiles__icon">
              <WebLink
                href={getStoryboardEditUrl(currentUser.screenname, section.id)}
                onClick={() =>
                  usageSetNavFrom(
                    USAGE_EVENT_NAMES.CURATOR_PRO_ENTER,
                    this.props.navFrom,
                  )
                }
              >
                <EditIcon color="#fff" />
              </WebLink>
            </span>
          )}
        </div>
      </div>
    );
  }
}

StoryboardTile.propTypes = {
  section: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  selectedId: PropTypes.string,
  isOwnProfile: PropTypes.bool,
  shownInFeed: PropTypes.bool,
  navFrom: PropTypes.string,
  usageSetNavFrom: PropTypes.func.isRequired,
  usageTrackSectionTap: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};

StoryboardTile.defaultProps = {
  selectedId: null,
  currentUser: null,
  isOwnProfile: false,
  shownInFeed: false,
  navFrom: null,
};

export default connector(
  connectUsageTrackSectionTap,
  connectUsageSetNavFrom,
  connectCurrentUser,
  connectLanguage,
)(withT(withHistory(withSelected(withNavFrom(StoryboardTile)))));
